import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MetricChart } from '@/components/charts/generic';
import ChartActionsMenu from '@/components/molecules/ChartActionsMenu';
import ContentBlock from '@/components/molecules/ContentBlock';
import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import EmptyState from '@/components/molecules/EmptyState';
import type { EnrichedBodyWoundsData } from '@/domain/welfare/types';
import chartBlockStyles from '@/routes/Client/Cage/pages/Health/components/HealthDetailChartContentBlock.module.scss';
import { HEALTH_DEFAULT_DATE_RANGE_PRESET_ID } from '@/routes/Client/Cage/pages/Health/components/healthDetailChartConstants';
import { WOUND_GRAPH_COLORS } from '@/routes/Client/Cage/pages/Health/HealthWounds/HealthWoundsBodyGraphs/healthWoundsGraphHelpers';

import {
    useWelfareWoundGraphChart,
    type WelfareWoundSeriesConfig,
} from './useWelfareWoundGraphChart';

import styles from './CageWelfareGraphs.module.scss';

const WOUND_GRAPH_CHART_HEIGHT = 360;

export interface CageWelfareGraphsPageContentProps {
    fishHealthData?: EnrichedBodyWoundsData;
    dateFrom?: string;
    dateTo?: string;
    onDateChange?: (data: DateRange) => void;
}

interface WoundGraphCardProps {
    title: string;
    chartName: string;
    bodyWounds: EnrichedBodyWoundsData;
    woundSeries: WelfareWoundSeriesConfig[];
}

function WoundGraphCard(props: WoundGraphCardProps) {
    const { title, chartName, bodyWounds, woundSeries } = props;
    const { t } = useTranslation();

    const chart = useWelfareWoundGraphChart({ bodyWounds, woundSeries });

    const hasNoData = bodyWounds.days.length === 0;

    return (
        <ContentBlock
            title={title}
            size="compact"
            spacing="none"
            className={chartBlockStyles.chartBlock}
            containerRef={chart.fullScreenContainerRef}
            headerActions={
                <ChartActionsMenu
                    chartRef={chart.chartRef}
                    chartName={chartName}
                    csvExport={false}
                    isFullScreen={chart.isFullScreen}
                    disabled={hasNoData}
                    onFullScreenToggle={chart.handleFullScreenToggle}
                />
            }>
            <div className={`${chartBlockStyles.chartBlockBody} ${styles.chartBody}`}>
                <MetricChart
                    kind="line"
                    chartRef={chart.chartRef}
                    data={chart.data}
                    options={chart.options}
                    height={WOUND_GRAPH_CHART_HEIGHT}
                    legendItems={chart.legendItems}
                    visibleSeries={chart.visibleSeries}
                    onToggleSeries={chart.toggleSeries}
                    noDataMessage={hasNoData ? t('common.empty.noData') : undefined}
                />
            </div>
        </ContentBlock>
    );
}

export function CageWelfareGraphsPageContent(props: CageWelfareGraphsPageContentProps) {
    const { fishHealthData, dateFrom, dateTo, onDateChange } = props;
    const { t } = useTranslation();

    // Same series as former OptoMixedChart BW welfare graphs. Color days also expose
    // `wound` (`any_wound`); active/healing are Color-only and omitted on bridged days.
    const typesSeries: WelfareWoundSeriesConfig[] = useMemo(
        () => [
            {
                label: t('fishHealthIndicators.wound'),
                color: WOUND_GRAPH_COLORS.active,
                getValue: (day) => day.wound,
            },
        ],
        [t]
    );

    const sizeSeries: WelfareWoundSeriesConfig[] = useMemo(
        () => [
            {
                label: t('fishHealthIndicators.bigWound'),
                color: WOUND_GRAPH_COLORS.large,
                getValue: (day) => day.bigWound,
            },
            {
                label: t('fishHealthIndicators.mediumWound'),
                color: WOUND_GRAPH_COLORS.medium,
                getValue: (day) => day.mediumWound,
            },
            {
                label: t('fishHealthIndicators.smallWound'),
                color: WOUND_GRAPH_COLORS.small,
                getValue: (day) => day.smallWound,
            },
        ],
        [t]
    );

    const distributionSeries: WelfareWoundSeriesConfig[] = useMemo(
        () => [
            {
                label: t('fishHealthIndicators.oneWound'),
                color: WOUND_GRAPH_COLORS.single,
                getValue: (day) => day.oneWound,
            },
            {
                label: t('fishHealthIndicators.moreThanOneWound'),
                color: WOUND_GRAPH_COLORS.multiple,
                getValue: (day) => day.moreThanOneWound,
            },
        ],
        [t]
    );

    if (!fishHealthData) {
        return <EmptyState title={t('common.empty.noData')} appearance="quiet" size="spacious" />;
    }

    return (
        <>
            <div className={styles.datePickerContainer}>
                <DateRangePicker
                    onDateChange={onDateChange}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    defaultPresetId={HEALTH_DEFAULT_DATE_RANGE_PRESET_ID}
                />
            </div>
            <div className={styles.chartsGrid}>
                <WoundGraphCard
                    title={t('cageWelfare.wounds.typesOfWounds')}
                    chartName="welfare-wounds-types-chart"
                    bodyWounds={fishHealthData}
                    woundSeries={typesSeries}
                />
                <WoundGraphCard
                    title={t('cageWelfare.wounds.sizeDistribution')}
                    chartName="welfare-wounds-size-chart"
                    bodyWounds={fishHealthData}
                    woundSeries={sizeSeries}
                />
                <WoundGraphCard
                    title={t('cageWelfare.wounds.distributionOfWounds')}
                    chartName="welfare-wounds-distribution-chart"
                    bodyWounds={fishHealthData}
                    woundSeries={distributionSeries}
                />
            </div>
        </>
    );
}
